import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { api } from "../api";
import type { CronJob, PanelUser } from "../types";

/** Split a 5-field cron schedule into parts. */
function parseSchedule(schedule: string): {
  minute: string;
  hour: string;
  day: string;
  month: string;
  weekday: string;
} {
  const parts = schedule.trim().split(/\s+/);
  if (parts.length < 5) {
    return { minute: "*", hour: "*", day: "*", month: "*", weekday: "*" };
  }
  return {
    minute: parts[0],
    hour: parts[1],
    day: parts[2],
    month: parts[3],
    weekday: parts[4]
  };
}

function buildSchedule(
  minute: string,
  hour: string,
  day: string,
  month: string,
  weekday: string
): string {
  return [minute, hour, day, month, weekday]
    .map((p) => (p.trim() === "" ? "*" : p.trim()))
    .join(" ");
}

type CronPreset = {
  label: string;
  minute: string;
  hour: string;
  day: string;
  month: string;
  weekday: string;
};

const COMMON_SETTINGS: CronPreset[] = [
  {
    label: "Common Settings",
    minute: "*",
    hour: "*",
    day: "*",
    month: "*",
    weekday: "*"
  },
  {
    label: "Once Per Minute (* * * * *)",
    minute: "*",
    hour: "*",
    day: "*",
    month: "*",
    weekday: "*"
  },
  {
    label: "Once Per Five Minutes (*/5 * * * *)",
    minute: "*/5",
    hour: "*",
    day: "*",
    month: "*",
    weekday: "*"
  },
  {
    label: "Twice Per Hour (0,30 * * * *)",
    minute: "0,30",
    hour: "*",
    day: "*",
    month: "*",
    weekday: "*"
  },
  {
    label: "Once Per Hour (0 * * * *)",
    minute: "0",
    hour: "*",
    day: "*",
    month: "*",
    weekday: "*"
  },
  {
    label: "Twice Per Day (0 0,12 * * *)",
    minute: "0",
    hour: "0,12",
    day: "*",
    month: "*",
    weekday: "*"
  },
  {
    label: "Once Per Day (0 0 * * *)",
    minute: "0",
    hour: "0",
    day: "*",
    month: "*",
    weekday: "*"
  },
  {
    label: "Once Per Week (0 0 * * 0)",
    minute: "0",
    hour: "0",
    day: "*",
    month: "*",
    weekday: "0"
  },
  {
    label: "On the 1st and 15th of the Month (0 0 1,15 * *)",
    minute: "0",
    hour: "0",
    day: "1,15",
    month: "*",
    weekday: "*"
  },
  {
    label: "Once Per Month (0 0 1 * *)",
    minute: "0",
    hour: "0",
    day: "1",
    month: "*",
    weekday: "*"
  },
  {
    label: "Once Per Year (0 0 1 1 *)",
    minute: "0",
    hour: "0",
    day: "1",
    month: "1",
    weekday: "*"
  }
];

const CRON_EXAMPLES: { schedule: string; description: string }[] = [
  { schedule: "* * * * *", description: "Every minute" },
  { schedule: "*/5 * * * *", description: "Every 5 minutes" },
  { schedule: "0,30 * * * *", description: "Twice an hour" },
  { schedule: "0 * * * *", description: "Once an hour" },
  { schedule: "0 0,12 * * *", description: "Twice a day (midnight & noon)" },
  { schedule: "0 0 * * *", description: "Once a day at midnight" },
  { schedule: "0 0 * * 0", description: "Once a week (Sunday midnight)" },
  { schedule: "0 0 1,15 * *", description: "1st and 15th of each month" },
  { schedule: "0 0 1 * *", description: "Once a month (1st at midnight)" },
  { schedule: "0 0 1 1 *", description: "Once a year (Jan 1 midnight)" }
];

function cronEmailKey(userId: string) {
  return `realpanel.cronEmail.${userId}`;
}

export function CronPage({
  accounts,
  currentUser,
  onRefreshAccounts
}: {
  accounts: PanelUser[];
  currentUser: PanelUser;
  onRefreshAccounts: () => Promise<void>;
}) {
  const staff =
    currentUser.role === "superadmin" || currentUser.role === "admin";
  const provisioned = accounts.filter(
    (a) => a.role === "user" && a.provisionStatus === "ready"
  );
  const [userId, setUserId] = useState(
    staff
      ? provisioned[0]?.id || ""
      : currentUser.provisionStatus === "ready"
        ? currentUser.id
        : ""
  );
  const [jobs, setJobs] = useState<CronJob[]>([]);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showExamples, setShowExamples] = useState(true);
  const [cronEmail, setCronEmail] = useState("");
  const [emailDraft, setEmailDraft] = useState("");

  // Add modal fields
  const [presetIdx, setPresetIdx] = useState(0);
  const [minute, setMinute] = useState("*");
  const [hour, setHour] = useState("*");
  const [day, setDay] = useState("*");
  const [month, setMonth] = useState("*");
  const [weekday, setWeekday] = useState("*");
  const [command, setCommand] = useState("");
  const [comment, setComment] = useState("");

  const selectedAccount = useMemo(
    () =>
      staff
        ? provisioned.find((a) => a.id === userId)
        : currentUser,
    [staff, provisioned, userId, currentUser]
  );

  const load = useCallback(async () => {
    if (!userId) return;
    setError("");
    try {
      const res = await api<{ items: CronJob[] }>(
        `/api/v1/cron?userId=${encodeURIComponent(userId)}`
      );
      setJobs(res.items || []);
      setSelectedIds([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!userId && provisioned[0]?.id) setUserId(provisioned[0].id);
  }, [provisioned, userId]);

  useEffect(() => {
    if (!userId) return;
    try {
      const saved = localStorage.getItem(cronEmailKey(userId)) || "";
      setCronEmail(saved);
      setEmailDraft(saved);
    } catch {
      setCronEmail("");
      setEmailDraft("");
    }
  }, [userId]);

  const openAdd = () => {
    setPresetIdx(0);
    setMinute("*");
    setHour("*");
    setDay("*");
    setMonth("*");
    setWeekday("*");
    setCommand("");
    setComment("");
    setError("");
    setShowAdd(true);
  };

  const applyPreset = (idx: number) => {
    setPresetIdx(idx);
    if (idx === 0) return; // "Common Settings" placeholder — don't overwrite
    const p = COMMON_SETTINGS[idx];
    if (!p) return;
    setMinute(p.minute);
    setHour(p.hour);
    setDay(p.day);
    setMonth(p.month);
    setWeekday(p.weekday);
  };

  const addJob = async (e: FormEvent) => {
    e.preventDefault();
    if (!userId) return;
    setError("");
    setMsg("");
    setBusy(true);
    try {
      const schedule = buildSchedule(minute, hour, day, month, weekday);
      await api("/api/v1/cron", {
        method: "POST",
        body: JSON.stringify({
          userId,
          schedule,
          command: command.trim(),
          comment: comment.trim() || null
        })
      });
      setMsg("Cron job added successfully.");
      setShowAdd(false);
      setCommand("");
      setComment("");
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (job: CronJob) => {
    try {
      await api(`/api/v1/cron/${job.id}`, {
        method: "PATCH",
        body: JSON.stringify({ enabled: !job.enabled })
      });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const removeOne = async (id: string) => {
    if (!confirm("Delete this cron job?")) return;
    try {
      await api(`/api/v1/cron/${id}`, { method: "DELETE" });
      setMsg("Cron job deleted.");
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const deleteSelected = async () => {
    if (selectedIds.length === 0) return;
    if (
      !confirm(
        `Delete ${selectedIds.length} selected cron job${
          selectedIds.length === 1 ? "" : "s"
        }?`
      )
    ) {
      return;
    }
    setBusy(true);
    setError("");
    try {
      for (const id of selectedIds) {
        await api(`/api/v1/cron/${id}`, { method: "DELETE" });
      }
      setMsg(
        `Deleted ${selectedIds.length} cron job${
          selectedIds.length === 1 ? "" : "s"
        }.`
      );
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === jobs.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(jobs.map((j) => j.id));
    }
  };

  const saveEmail = (e: FormEvent) => {
    e.preventDefault();
    if (!userId) return;
    const val = emailDraft.trim();
    try {
      if (val) localStorage.setItem(cronEmailKey(userId), val);
      else localStorage.removeItem(cronEmailKey(userId));
      setCronEmail(val);
      setMsg(
        val
          ? `Cron notification email set to ${val}.`
          : "Cron notification email cleared."
      );
    } catch {
      setError("Could not save email preference in this browser.");
    }
  };

  if (!userId) {
    return (
      <div className="cj-page">
        <div className="dm-hero">
          <div className="dm-hero-icon" aria-hidden>
            ⏱
          </div>
          <div>
            <h1>Cron Jobs</h1>
            <p>Provision an end-user account first to manage scheduled tasks.</p>
          </div>
          <div className="dm-hero-actions">
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => void onRefreshAccounts()}
            >
              Refresh
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cj-page">
      <div className="dm-hero">
        <div className="dm-hero-icon" aria-hidden>
          ⏱
        </div>
        <div>
          <h1>Cron Jobs</h1>
          <p>
            Schedule commands to run automatically
            {selectedAccount?.username
              ? ` · ${selectedAccount.username}`
              : ""}
          </p>
        </div>
        <div className="dm-hero-actions">
          {staff ? (
            <select
              className="inline-select"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              aria-label="Account"
            >
              {provisioned.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.username}
                </option>
              ))}
            </select>
          ) : null}
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => void load()}
          >
            Refresh
          </button>
          <button type="button" className="btn btn-primary" onClick={openAdd}>
            + Add a Cron Job
          </button>
        </div>
      </div>

      {msg ? <div className="success-banner">{msg}</div> : null}
      {error && !showAdd ? <div className="error-banner">{error}</div> : null}

      {/* Cron Email */}
      <div className="dm-card cj-section">
        <div className="cj-section-head">
          <h2>Cron Email</h2>
        </div>
        <div className="cj-section-body">
          <p className="cj-hint">
            Enter an email address for the cron daemon to send emails to if a
            cron job generates output. Leave blank to disable email
            notifications for this account.
          </p>
          <form className="cj-email-row" onSubmit={saveEmail}>
            <input
              type="email"
              className="cj-input"
              placeholder="you@example.com"
              value={emailDraft}
              onChange={(e) => setEmailDraft(e.target.value)}
              aria-label="Cron notification email"
            />
            <button type="submit" className="btn btn-primary">
              Update Email
            </button>
          </form>
          {cronEmail ? (
            <p className="cj-email-current">
              Current: <strong>{cronEmail}</strong>
            </p>
          ) : null}
        </div>
      </div>

      {/* Current Cron Jobs */}
      <div className="dm-card cj-section">
        <div className="cj-section-head cj-section-head-row">
          <h2>Current Cron Jobs</h2>
          <div className="cj-section-actions">
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={selectedIds.length === 0 || busy}
              onClick={() => void deleteSelected()}
            >
              Delete Selected
              {selectedIds.length > 0 ? ` (${selectedIds.length})` : ""}
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={openAdd}
            >
              Add a Cron Job
            </button>
          </div>
        </div>
        <div className="dm-table-wrap">
          <table className="dm-table cj-table">
            <thead>
              <tr>
                <th className="dm-check-col">
                  <input
                    type="checkbox"
                    checked={
                      jobs.length > 0 && selectedIds.length === jobs.length
                    }
                    onChange={toggleSelectAll}
                    disabled={jobs.length === 0}
                    aria-label="Select all"
                  />
                </th>
                <th>Minute</th>
                <th>Hour</th>
                <th>Day</th>
                <th>Month</th>
                <th>Weekday</th>
                <th>Command</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {jobs.length === 0 ? (
                <tr>
                  <td colSpan={9} className="dm-empty">
                    No cron jobs yet. Click{" "}
                    <strong>Add a Cron Job</strong> to create one.
                  </td>
                </tr>
              ) : (
                jobs.map((j) => {
                  const p = parseSchedule(j.schedule);
                  return (
                    <tr key={j.id} className={j.enabled ? "" : "cj-row-off"}>
                      <td className="dm-check-col">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(j.id)}
                          onChange={() => toggleSelect(j.id)}
                          aria-label={`Select ${j.command}`}
                        />
                      </td>
                      <td className="mono cj-field-cell">{p.minute}</td>
                      <td className="mono cj-field-cell">{p.hour}</td>
                      <td className="mono cj-field-cell">{p.day}</td>
                      <td className="mono cj-field-cell">{p.month}</td>
                      <td className="mono cj-field-cell">{p.weekday}</td>
                      <td>
                        <div className="mono cj-cmd">{j.command}</div>
                        {j.comment ? (
                          <div className="dm-sub">{j.comment}</div>
                        ) : null}
                      </td>
                      <td>
                        <button
                          type="button"
                          className={`dm-toggle ${j.enabled ? "on" : ""}`}
                          onClick={() => void toggle(j)}
                          title={j.enabled ? "Disable" : "Enable"}
                        >
                          <span className="dm-toggle-knob" />
                          {j.enabled ? "ON" : "OFF"}
                        </button>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="dm-icon-btn danger"
                          onClick={() => void removeOne(j.id)}
                          title="Delete"
                          aria-label="Delete cron job"
                        >
                          🗑
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Examples */}
      <div className="dm-card cj-section">
        <button
          type="button"
          className="cj-section-head cj-section-toggle"
          onClick={() => setShowExamples((v) => !v)}
        >
          <h2>Cron Job Examples</h2>
          <span className="cj-chevron">{showExamples ? "▾" : "▸"}</span>
        </button>
        {showExamples ? (
          <div className="cj-section-body">
            <p className="cj-hint">
              Cron uses five fields:{" "}
              <code className="mono">minute hour day month weekday</code>. Use
              these patterns as a guide when building your schedule.
            </p>
            <div className="dm-table-wrap">
              <table className="dm-table cj-examples">
                <thead>
                  <tr>
                    <th>Schedule</th>
                    <th>Meaning</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {CRON_EXAMPLES.map((ex) => (
                    <tr key={ex.schedule}>
                      <td className="mono">{ex.schedule}</td>
                      <td>{ex.description}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => {
                            const p = parseSchedule(ex.schedule);
                            setMinute(p.minute);
                            setHour(p.hour);
                            setDay(p.day);
                            setMonth(p.month);
                            setWeekday(p.weekday);
                            setPresetIdx(0);
                            setShowAdd(true);
                          }}
                        >
                          Use
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </div>

      {/* Add Cron Job modal */}
      {showAdd ? (
        <div
          className="cj-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cj-add-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowAdd(false);
          }}
        >
          <div className="cj-modal">
            <div className="cj-modal-head">
              <div className="cj-modal-title-wrap">
                <span className="cj-modal-icon" aria-hidden>
                  ⏱
                </span>
                <h2 id="cj-add-title">Add a Cron Job</h2>
              </div>
              <button
                type="button"
                className="cj-modal-close"
                onClick={() => setShowAdd(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <form className="cj-modal-body" onSubmit={addJob}>
              {error ? <div className="error-banner">{error}</div> : null}

              <div className="cj-field">
                <label htmlFor="cj-common">Common Settings</label>
                <select
                  id="cj-common"
                  className="cj-input"
                  value={presetIdx}
                  onChange={(e) => applyPreset(Number(e.target.value))}
                >
                  {COMMON_SETTINGS.map((p, i) => (
                    <option key={p.label} value={i}>
                      {p.label}
                    </option>
                  ))}
                </select>
                <small>
                  Pick a preset to fill minute / hour / day / month / weekday,
                  or edit the fields below manually.
                </small>
              </div>

              <div className="cj-field-grid">
                <div className="cj-field">
                  <label htmlFor="cj-minute">Minute</label>
                  <input
                    id="cj-minute"
                    className="cj-input mono"
                    value={minute}
                    onChange={(e) => {
                      setMinute(e.target.value);
                      setPresetIdx(0);
                    }}
                    required
                  />
                  <small>
                    Valid: 0–59 · <code>*</code> every · <code>*/5</code> every
                    5 · <code>0,30</code> list
                  </small>
                </div>
                <div className="cj-field">
                  <label htmlFor="cj-hour">Hour</label>
                  <input
                    id="cj-hour"
                    className="cj-input mono"
                    value={hour}
                    onChange={(e) => {
                      setHour(e.target.value);
                      setPresetIdx(0);
                    }}
                    required
                  />
                  <small>
                    Valid: 0–23 · <code>*</code> every hour ·{" "}
                    <code>0,12</code> midnight &amp; noon
                  </small>
                </div>
                <div className="cj-field">
                  <label htmlFor="cj-day">Day</label>
                  <input
                    id="cj-day"
                    className="cj-input mono"
                    value={day}
                    onChange={(e) => {
                      setDay(e.target.value);
                      setPresetIdx(0);
                    }}
                    required
                  />
                  <small>
                    Valid: 1–31 · <code>*</code> every day ·{" "}
                    <code>1,15</code> 1st &amp; 15th
                  </small>
                </div>
                <div className="cj-field">
                  <label htmlFor="cj-month">Month</label>
                  <input
                    id="cj-month"
                    className="cj-input mono"
                    value={month}
                    onChange={(e) => {
                      setMonth(e.target.value);
                      setPresetIdx(0);
                    }}
                    required
                  />
                  <small>
                    Valid: 1–12 · <code>*</code> every month · <code>1</code>{" "}
                    January
                  </small>
                </div>
                <div className="cj-field">
                  <label htmlFor="cj-weekday">Weekday</label>
                  <input
                    id="cj-weekday"
                    className="cj-input mono"
                    value={weekday}
                    onChange={(e) => {
                      setWeekday(e.target.value);
                      setPresetIdx(0);
                    }}
                    required
                  />
                  <small>
                    Valid: 0–6 (0 = Sunday) · <code>*</code> every day
                  </small>
                </div>
              </div>

              <div className="cj-field">
                <label htmlFor="cj-command">Command</label>
                <input
                  id="cj-command"
                  className="cj-input mono"
                  value={command}
                  onChange={(e) => setCommand(e.target.value)}
                  placeholder="/usr/bin/php /home/user/public_html/cron.php"
                  required
                />
                <small>
                  Full path to the script or binary to run. Example:{" "}
                  <code className="mono">
                    /usr/bin/php ~/public_html/cron.php
                  </code>
                </small>
              </div>

              <div className="cj-field">
                <label htmlFor="cj-comment">
                  Comment <span className="cj-optional">(optional)</span>
                </label>
                <input
                  id="cj-comment"
                  className="cj-input"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="e.g. nightly backup"
                />
              </div>

              <div className="cj-preview">
                <span className="cj-preview-label">Schedule preview</span>
                <code className="mono">
                  {buildSchedule(minute, hour, day, month, weekday)}
                </code>
              </div>

              <div className="cj-modal-actions">
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => setShowAdd(false)}
                  disabled={busy}
                >
                  Close
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={busy || !command.trim()}
                >
                  {busy ? "Adding…" : "Add Cron Job"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}
